/**
 * TUS Upload - Надёжная загрузка файлов с возобновлением
 * Использует протокол TUS для resumable uploads
 * Автор: AI Market Hub
 * 
 * Преимущества:
 * - Автоматическое возобновление после обрыва соединения
 * - Работает даже после перезагрузки страницы
 * - Экспоненциальный backoff при ошибках
 * - Прогресс загрузки в реальном времени
 */

import * as tus from 'tus-js-client';

// Конфигурация TUS
const TUS_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB чанки
const TUS_RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000]; // Задержки между попытками

// Хранилище для активных загрузок (для отмены)
const activeUploads = new Map();

/**
 * Загружает файл с использованием TUS протокола
 * @param {File} file - Файл для загрузки
 * @param {string} apiUrl - Базовый URL API
 * @param {Object} options - Опции загрузки
 * @param {string} options.userId - ID пользователя
 * @param {string} options.authToken - Токен авторизации
 * @param {Function} options.onProgress - Callback прогресса (percent, uploadedBytes, totalBytes)
 * @param {Function} options.onSuccess - Callback успешной загрузки (videoId, videoPath)
 * @param {Function} options.onError - Callback ошибки (error)
 * @param {Function} options.onRetry - Callback повторной попытки (attempt, delay)
 * @param {Function} options.onStatusChange - Callback смены статуса ('uploading', 'paused', 'complete', 'error')
 * @returns {Promise<Object>} Результат загрузки { videoId, videoPath }
 */
export function uploadWithTus(file, apiUrl, options = {}) {
  const {
    userId,
    authToken,
    onProgress,
    onSuccess,
    onError,
    onRetry,
    onStatusChange
  } = options;

  return new Promise((resolve, reject) => {
    // Проверяем обязательные параметры
    if (!userId) {
      const error = new Error('userId обязателен для TUS загрузки');
      if (onError) onError(error);
      reject(error);
      return;
    }

    // Формируем заголовки авторизации
    const headers = {};
    if (authToken) {
      if (authToken.includes('.')) {
        headers['Authorization'] = `Bearer ${authToken}`;
      } else {
        headers['x-session-token'] = authToken;
      }
    }

    // Уникальный fingerprint для возобновления загрузки
    const fingerprint = `tus-${file.name}-${file.size}-${file.lastModified}-${userId}`;

    console.log(`[TUS] 🚀 Начало загрузки: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
    if (onStatusChange) onStatusChange('uploading');

    // Переменные для хранения videoId и videoPath из заголовков ответа
    let savedVideoId = null;
    let savedVideoPath = null;
    
    // Создаём TUS upload
    const upload = new tus.Upload(file, {
      endpoint: `${apiUrl}/youtube/tus`,
      retryDelays: TUS_RETRY_DELAYS,
      chunkSize: TUS_CHUNK_SIZE,
      headers: headers,
      
      // Метаданные для сервера
      metadata: {
        filename: file.name,
        filetype: file.type || 'video/mp4',
        userId: userId
      },

      // Fingerprint для возобновления
      fingerprint: () => Promise.resolve(fingerprint),
      
      // Хранилище для возобновления (localStorage)
      urlStorage: {
        findAllUploads: () => {
          try {
            const stored = localStorage.getItem('tus-uploads');
            return Promise.resolve(stored ? JSON.parse(stored) : []);
          } catch (e) {
            return Promise.resolve([]);
          }
        },
        findUploadsByFingerprint: (fingerprint) => {
          try {
            const stored = localStorage.getItem('tus-uploads');
            const uploads = stored ? JSON.parse(stored) : [];
            return Promise.resolve(uploads.filter(u => u.fingerprint === fingerprint));
          } catch (e) {
            return Promise.resolve([]);
          }
        },
        removeUpload: (urlStorageKey) => {
          try {
            const stored = localStorage.getItem('tus-uploads');
            const uploads = stored ? JSON.parse(stored) : [];
            const filtered = uploads.filter(u => u.urlStorageKey !== urlStorageKey);
            localStorage.setItem('tus-uploads', JSON.stringify(filtered));
          } catch (e) {
            // Игнорируем ошибки localStorage
          }
          return Promise.resolve();
        },
        addUpload: (fingerprint, upload) => {
          try {
            const stored = localStorage.getItem('tus-uploads');
            const uploads = stored ? JSON.parse(stored) : [];
            const urlStorageKey = `tus-key-${Date.now()}`;
            uploads.push({ fingerprint, ...upload, urlStorageKey, createdAt: Date.now() });
            // Очищаем старые записи (старше 7 дней)
            const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
            const filtered = uploads.filter(u => u.createdAt > weekAgo);
            localStorage.setItem('tus-uploads', JSON.stringify(filtered));
            return Promise.resolve(urlStorageKey);
          } catch (e) {
            return Promise.resolve(null);
          }
        }
      },

      // Callback прогресса
      onProgress: (bytesUploaded, bytesTotal) => {
        const percent = (bytesUploaded / bytesTotal) * 100;
        console.log(`[TUS] 📊 Прогресс: ${percent.toFixed(1)}% (${(bytesUploaded / 1024 / 1024).toFixed(1)}/${(bytesTotal / 1024 / 1024).toFixed(1)}MB)`);
        
        if (onProgress) {
          onProgress(percent, bytesUploaded, bytesTotal);
        }
      },

      // Callback успешной загрузки
      onSuccess: async () => {
        console.log(`[TUS] ✅ Загрузка завершена: ${file.name}`);
        if (onStatusChange) onStatusChange('complete');
        
        // Извлекаем uploadId из URL загрузки
        // TUS возвращает URL вида: /api/youtube/tus/uploadId
        const uploadUrl = upload.url;
        const uploadId = uploadUrl ? uploadUrl.split('/').pop() : null;
        
        // Если не получили заголовки из onAfterResponse, пытаемся получить через HEAD запрос
        if (!savedVideoId && uploadUrl) {
          try {
            const headResponse = await fetch(uploadUrl, {
              method: 'HEAD',
              headers: headers
            });
            
            if (headResponse.ok) {
              const videoIdHeader = headResponse.headers.get('X-Video-Id');
              const videoPathHeader = headResponse.headers.get('X-Video-Path');
              
              if (videoIdHeader) {
                savedVideoId = videoIdHeader;
                console.log(`[TUS] ✅ Получен videoId через HEAD: ${savedVideoId}`);
              }
              if (videoPathHeader) {
                savedVideoPath = videoPathHeader;
                console.log(`[TUS] ✅ Получен videoPath через HEAD: ${savedVideoPath}`);
              }
            }
          } catch (e) {
            console.warn('[TUS] Не удалось получить заголовки через HEAD:', e);
          }
        }
        
        // Удаляем из активных загрузок
        activeUploads.delete(fingerprint);
        
        // Удаляем запись из localStorage, чтобы избежать повторного запуска
        try {
          const stored = localStorage.getItem('tus-uploads');
          if (stored) {
            const uploads = JSON.parse(stored);
            const filtered = uploads.filter(u => {
              // Удаляем все записи с таким же fingerprint или uploadId
              return u.fingerprint !== fingerprint && 
                     (!uploadUrl || !u.url || !u.url.includes(uploadId));
            });
            localStorage.setItem('tus-uploads', JSON.stringify(filtered));
            console.log('[TUS] 🧹 Очищена запись из localStorage');
          }
        } catch (e) {
          console.warn('[TUS] Ошибка очистки localStorage:', e);
        }
        
        // Используем сохранённые videoId и videoPath из заголовков ответа
        const result = { 
          success: true,
          uploadId,
          videoId: savedVideoId ? parseInt(savedVideoId) : null,
          videoPath: savedVideoPath,
          fileName: file.name,
          fileSize: file.size
        };
        
        console.log('[TUS] Результат загрузки:', result);
        
        if (onSuccess) onSuccess(result);
        resolve(result);
      },

      // Callback ошибки
      onError: (error) => {
        console.error(`[TUS] ❌ Ошибка загрузки: ${error.message}`);
        if (onStatusChange) onStatusChange('error');
        
        // Удаляем из активных загрузок
        activeUploads.delete(fingerprint);
        
        if (onError) onError(error);
        reject(error);
      },

      // Callback перед повторной попыткой
      onBeforeRequest: (req) => {
        // Обновляем токен авторизации перед каждым запросом
        if (authToken) {
          if (authToken.includes('.')) {
            req.setHeader('Authorization', `Bearer ${authToken}`);
          } else {
            req.setHeader('x-session-token', authToken);
          }
        }
      },

      // Callback после ошибки (перед retry)
      onShouldRetry: (err, retryAttempt, options) => {
        const status = err.originalResponse?.getStatus();
        
        // Не повторяем при 4xx ошибках (кроме 429)
        if (status && status >= 400 && status < 500 && status !== 429) {
          console.log(`[TUS] ⛔ Ошибка ${status}, повтор невозможен`);
          return false;
        }
        
        // Повторяем при сетевых ошибках и 5xx
        console.log(`[TUS] 🔄 Попытка ${retryAttempt + 1}/${TUS_RETRY_DELAYS.length}`);
        if (onRetry) {
          onRetry(retryAttempt + 1, TUS_RETRY_DELAYS[retryAttempt] || 60000);
        }
        if (onStatusChange) onStatusChange('retrying');
        
        return true;
      },

      // Callback после успешного запроса
      onAfterResponse: (req, res) => {
        // Логируем ответы для отладки
        const status = res.getStatus();
        
        // Сохраняем videoId и videoPath из заголовков ответа
        // TUS отправляет 204 No Content при завершении загрузки
        if (status === 204) {
          try {
            const videoIdHeader = res.getHeader('X-Video-Id');
            const videoPathHeader = res.getHeader('X-Video-Path');
            
            if (videoIdHeader) {
              savedVideoId = videoIdHeader;
              console.log(`[TUS] ✅ Получен videoId из заголовка: ${savedVideoId}`);
            }
            if (videoPathHeader) {
              savedVideoPath = videoPathHeader;
              console.log(`[TUS] ✅ Получен videoPath из заголовка: ${savedVideoPath}`);
            }
          } catch (e) {
            console.warn('[TUS] Ошибка чтения заголовков:', e);
          }
        }
        
        if (status >= 400) {
          console.warn(`[TUS] ⚠️ Ответ сервера: ${status}`);
        }
      }
    });

    // Сохраняем upload для возможности отмены
    activeUploads.set(fingerprint, upload);

    // Проверяем наличие предыдущих загрузок для возобновления
    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length > 0) {
        console.log(`[TUS] ♻️ Найдена предыдущая загрузка, возобновляем...`);
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      
      // Начинаем загрузку
      upload.start();
    }).catch((err) => {
      console.warn('[TUS] Не удалось проверить предыдущие загрузки:', err);
      upload.start();
    });
  });
}

/**
 * Приостанавливает загрузку
 * @param {File} file - Файл загрузки
 * @param {string} userId - ID пользователя
 */
export function pauseTusUpload(file, userId) {
  const fingerprint = `tus-${file.name}-${file.size}-${file.lastModified}-${userId}`;
  const upload = activeUploads.get(fingerprint);
  
  if (upload) {
    upload.abort();
    console.log(`[TUS] ⏸️ Загрузка приостановлена: ${file.name}`);
    return true;
  }
  
  return false;
}

/**
 * Возобновляет загрузку
 * @param {File} file - Файл загрузки
 * @param {string} userId - ID пользователя
 */
export function resumeTusUpload(file, userId) {
  const fingerprint = `tus-${file.name}-${file.size}-${file.lastModified}-${userId}`;
  const upload = activeUploads.get(fingerprint);
  
  if (upload) {
    upload.start();
    console.log(`[TUS] ▶️ Загрузка возобновлена: ${file.name}`);
    return true;
  }
  
  return false;
}

/**
 * Отменяет загрузку
 * @param {File} file - Файл загрузки
 * @param {string} userId - ID пользователя
 */
export function cancelTusUpload(file, userId) {
  const fingerprint = `tus-${file.name}-${file.size}-${file.lastModified}-${userId}`;
  const upload = activeUploads.get(fingerprint);
  
  if (upload) {
    upload.abort(true); // true = удалить данные на сервере
    activeUploads.delete(fingerprint);
    
    // Очищаем localStorage
    try {
      const stored = localStorage.getItem('tus-uploads');
      const uploads = stored ? JSON.parse(stored) : [];
      const filtered = uploads.filter(u => u.fingerprint !== fingerprint);
      localStorage.setItem('tus-uploads', JSON.stringify(filtered));
    } catch (e) {
      // Игнорируем
    }
    
    console.log(`[TUS] ❌ Загрузка отменена: ${file.name}`);
    return true;
  }
  
  return false;
}

/**
 * Проверяет наличие незавершённых загрузок
 * @param {File} file - Файл
 * @param {string} userId - ID пользователя
 * @returns {boolean} true если есть незавершённая загрузка
 */
export function hasPendingTusUpload(file, userId) {
  const fingerprint = `tus-${file.name}-${file.size}-${file.lastModified}-${userId}`;
  
  try {
    const stored = localStorage.getItem('tus-uploads');
    const uploads = stored ? JSON.parse(stored) : [];
    return uploads.some(u => u.fingerprint === fingerprint);
  } catch (e) {
    return false;
  }
}

/**
 * Очищает все сохранённые загрузки
 */
export function clearAllTusUploads() {
  try {
    localStorage.removeItem('tus-uploads');
    activeUploads.clear();
    console.log('[TUS] 🧹 Все сохранённые загрузки очищены');
  } catch (e) {
    // Игнорируем
  }
}

export { TUS_CHUNK_SIZE, TUS_RETRY_DELAYS };

